import { useState } from "react";

import { ButtonsGroupChoice, ButtonsGroupOption } from "@bps/fluent-ui";
import { NoPermissionError } from "@components/errors/NoPermissionError";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { SectionTitle } from "@components/SectionTitle";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  PremSettings,
  PremSettingsApiVersion
} from "@libs/api/gateways/tg/tg-ops-gateway.dtos";
import { usePremSettingsQuery } from "@libs/api/gateways/tg/tg-ops-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { HostName } from "../../ttm/tenant-configuration/TenantConfigurationCard.types";
import { PremSettingsForm } from "./prem-settings-form";

interface PremSettingsPageProps {
  tenant: Tenant;
}

const premSettingsVersionOptions: ButtonsGroupOption<
  PremSettingsApiVersion
>[] = [
  { key: "v1", text: "API Version 1 (Agent-based)" },
  { key: "v2", text: "API Version 2 (Field controller-based)" }
];

const isProductionHost = (hostName: string) => {
  return hostName.startsWith(HostName.Proda);
};

export const PremSettingsPage = ({ tenant }: PremSettingsPageProps) => {
  const showApiVersionSelector = !isProductionHost(window.location.hostname);

  const [apiVersion, setApiVersion] = useState<PremSettingsApiVersion>("v1");
  const premSettingsQuery = usePremSettingsQuery(tenant.id, apiVersion);
  const { data: hasPreRelease } = usePermissionsCheckQuery(
    [
      Permissions.OpsConsolePreRelease,
      Permissions.ProsSiteManagementRead,
      Permissions.ProsSiteManagementWrite
    ],
    "or"
  );

  return (
    <>
      <SectionTitle>Bp Premier Settings</SectionTitle>
      {hasPreRelease ? (
        <>
          {showApiVersionSelector && (
            <ButtonsGroupChoice
              options={premSettingsVersionOptions}
              onChange={option => {
                if (option === "v1" || option === "v2") {
                  setApiVersion(option);
                }
              }}
              value={apiVersion}
            />
          )}
          <QueryStateIndicator<PremSettings[]>
            {...premSettingsQuery}
            loadingTitle="Loading settings from Premier ..."
          >
            {premSettings => (
              <PremSettingsForm
                premSettings={premSettings}
                siteId={tenant.id}
                apiVersion={apiVersion}
              />
            )}
          </QueryStateIndicator>
        </>
      ) : (
        <NoPermissionError />
      )}
    </>
  );
};
